import { useTranslation } from 'react-i18next';

// Metric definitions shown in the ⓘ tooltips (Enhanced tier)
export default function useMetricTooltips() {
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.DataEnrichment.Tooltips' });

  return {
    creditDebitRatio: t('creditDebitRatio', {
      defaultValue:
        'Total credits divided by total debits. A ratio above 1.0 means more money coming in than going out. Used to assess whether cash flow is net positive.',
    }),
    transactionFrequency: t('transactionFrequency', {
      defaultValue:
        'Average number of days between all credits and debits. A lower number means more frequent activity; a higher number suggests less regular cash flow.',
    }),
    largeTransactions: t('largeTransactions', {
      defaultValue:
        "Number of individual transactions that exceeded 3× the account's average transaction amount. High counts may signal irregular or unpredictable financial behaviour.",
    }),
    avgCreditAmount: t('avgCreditAmount', {
      defaultValue:
        'The average dollar value of all credits into this account. Reflects typical deposit size over the analyzed period.',
    }),
    avgCreditsPerMonth: t('avgCreditsPerMonth', {
      defaultValue:
        'Average number of credit transactions received per month. Helps assess how consistently and frequently income or deposits arrive.',
    }),
    percentile90Credit: t('percentile90Credit', {
      defaultValue: '90% of all credits were below this amount. Useful for identifying the upper range of typical deposits.',
    }),
    incomeStability: t('incomeStability', {
      defaultValue:
        'Measures how consistent income deposits are over time. A higher value indicates more month-to-month fluctuation; a lower number signals predictable, steady income.',
    }),
    incomeVariability: t('incomeVariability', {
      defaultValue:
        'How much income changes from month to month, shown as a percentage of the average income. Lower means steadier income: under 10% is excellent (very predictable), 10–20% is good, 21–30% is fair, and above 30% means income arrives in unpredictable amounts.',
    }),
    nsfFee: t('nsfFee', {
      defaultValue:
        'Total dollar amount of non-sufficient fund (NSF) fees charged to this account over the selected period.',
    }),
    nsfRate: t('nsfRate', {
      defaultValue:
        'The percentage of transactions that resulted in an NSF. A higher rate indicates a pattern of insufficient funds relative to overall account activity.',
    }),
    avgNsfAmount: t('avgNsfAmount', {
      defaultValue:
        'The average amount of NSF transactions. Helps distinguish between small accidental overdrafts and larger, more significant funding gaps.',
    }),
    percentile90Debit: t('percentile90Debit', {
      defaultValue: '90% of all debits were below this amount. Useful for understanding the upper range of typical payments.',
    }),
    overdraft: t('overdraft', {
      defaultValue: 'Total number of times the account balance went negative.',
    }),
    bankFee: t('bankFee', {
      defaultValue: 'Total non-NSF bank fees charged to this account. Excludes NSF fees, which are tracked separately.',
    }),
    avgDaysBetween: t('avgDaysBetween', {
      defaultValue:
        'Average number of days between consecutive transactions of this type. A lower number indicates more frequent, regular transactions.',
    }),
  };
}
